import { useState, useRef, useEffect } from "react";
import { api, formatApiError, openSecureFile } from "../lib/api";
import { useDropZone } from "../lib/useDropZone";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { AuthImage } from "./AuthImage";
import { MessageCircle, Send, ChevronDown, Paperclip, Camera, UserPlus, X, FileText, Phone, Download, Upload, Search, Loader2 } from "lucide-react";
import { toast } from "sonner";

const fmt = (iso) => (iso ? new Date(iso).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }) : "");
const isImg = (a) => (a.content_type || "").startsWith("image/");

export function TicketMessaggi({ ticketId, condominioId, messaggi = [], role, onUpdate, endpoint, unreadCount = 0, onRead, readonly = false, fornitoreTitolareId, tecniciCondivisiIds = [], variant = "fornitore" }) {
  // Iter122fc · variant può essere "fornitore" (Studio↔Fornitore) o "condomino" (Studio↔Condòmino segnalante).
  // Nell'endpoint fallback usiamo il path corretto in base alla variante.
  const postUrl = endpoint || (variant === "condomino" ? `/tickets/${ticketId}/messaggi-condomino` : `/tickets/${ticketId}/messaggi`);
  const mine = role === "fornitore" ? "Fornitore" : (role === "condomino" ? "Condomino" : "Studio");
  // Iter122k32 · Label chat: per Credius mostra "Comunicazioni Studio-Avvocato" a prescindere dal ruolo.
  const isCredius = variant === "credius";
  const other = variant === "condomino"
    ? (mine === "Studio" ? "il condòmino segnalante" : "lo Studio")
    : (mine === "Studio" ? "il fornitore" : "lo Studio");
  const titleLabel = isCredius ? "Comunicazioni Studio-Avvocato" : `Comunicazioni con ${other}`;
  const [open, setOpen] = useState(messaggi.length > 0 && unreadCount === 0);
  const [testo, setTesto] = useState("");
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [allegati, setAllegati] = useState([]);
  const [contatti, setContatti] = useState([]);
  const [showContatto, setShowContatto] = useState(false);
  const [cNome, setCNome] = useState("");
  const [cTel, setCTel] = useState("");
  // Iter122fl · Rubrica app (condòmini o fornitori a seconda della variant)
  const [rubricaOpen, setRubricaOpen] = useState(false);
  const [rubricaLoading, setRubricaLoading] = useState(false);
  const [rubricaList, setRubricaList] = useState([]);
  const [rubricaError, setRubricaError] = useState("");
  const [rubricaSearch, setRubricaSearch] = useState("");
  const endRef = useRef(null);

  useEffect(() => { if (open) endRef.current?.scrollIntoView({ block: "nearest" }); }, [messaggi.length, open]);

  const upload = async (files) => {
    if (!files?.length) return;
    setUploading(true);
    try {
      for (const file of Array.from(files)) {
        const fd = new FormData();
        fd.append("file", file);
        const ep = file.type?.startsWith("image/") ? "/uploads" : "/uploads/documento";
        const r = await api.post(ep, fd, { headers: { "Content-Type": "multipart/form-data" } });
        setAllegati((p) => [...p, { id: r.data.id, original_filename: r.data.filename || file.name, content_type: r.data.content_type || file.type }]);
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Caricamento non riuscito");
    } finally {
      setUploading(false);
    }
  };

  const { dragProps, isDragOver } = useDropZone(upload, { disabled: uploading });

  // Iter122fl · Apre la rubrica applicativa: condòmini nella chat fornitore, fornitori nella chat condòmino.
  // Il vecchio comportamento (Contact Picker API del browser) usava la rubrica del telefono e non
  // era ciò che l'utente si aspettava: ora si apre un dialog con i contatti dell'app filtrabili.
  const rubricaLabel = variant === "condomino" ? "fornitori" : "condòmini";
  const loadRubrica = async () => {
    setRubricaLoading(true);
    setRubricaError("");
    try {
      if (variant === "condomino") {
        // Chat con condòmino: si allegano contatti di fornitori del tenant
        const r = await api.get("/fornitori");
        // Iter122fq · fix: /fornitori restituisce {fornitori:[...], total, returned}, non un array puro
        const arr = Array.isArray(r.data) ? r.data : (r.data?.fornitori || []);
        const list = arr.map((f) => ({
          key: f.id,
          nome: f.ragione_sociale || f.nome || "Fornitore",
          telefono: f.telefono || f.contatti || "",
          sottotitolo: [f.settore, f.citta].filter(Boolean).join(" · "),
        }));
        setRubricaList(list);
      } else {
        // Chat con fornitore: si allegano contatti di condòmini del condominio del ticket
        if (!condominioId) {
          setRubricaList([]);
          setRubricaError("Condominio non disponibile per questa manutenzione");
          return;
        }
        const r = await api.get(`/condomini/${condominioId}/contatti`);
        const list = (r.data || []).map((c, i) => ({
          key: c.id || c.soggetto_id || c.user_id || `c-${i}`,
          nome: c.nome || c.ragione_sociale || "Condòmino",
          telefono: c.telefono || c.phone || "",
          sottotitolo: [c.ruolo, c.unita].filter(Boolean).join(" · "),
        }));
        setRubricaList(list);
      }
    } catch (err) {
      setRubricaError(formatApiError(err.response?.data?.detail) || "Impossibile caricare la rubrica");
      setRubricaList([]);
    } finally {
      setRubricaLoading(false);
    }
  };

  const pickContatto = () => {
    setRubricaSearch("");
    setRubricaOpen(true);
    loadRubrica();
  };

  const selectFromRubrica = (item) => {
    setContatti((p) => [...p, { nome: item.nome, telefono: item.telefono || "" }]);
    setRubricaOpen(false);
  };

  const openManuale = () => {
    setRubricaOpen(false);
    setShowContatto(true);
  };

  const rubricaFiltered = rubricaList.filter((c) => {
    const q = rubricaSearch.trim().toLowerCase();
    if (!q) return true;
    return [c.nome, c.telefono, c.sottotitolo].filter(Boolean).join(" ").toLowerCase().includes(q);
  });

  const addContattoManuale = () => {
    const n = cNome.trim(), t = cTel.trim();
    if (!n && !t) return;
    setContatti((p) => [...p, { nome: n, telefono: t }]);
    setCNome(""); setCTel(""); setShowContatto(false);
  };

  const invia = async () => {
    const v = testo.trim();
    if (!v && allegati.length === 0 && contatti.length === 0) return;
    setBusy(true);
    try {
      const r = await api.post(postUrl, {
        testo: v, allegati_ids: allegati.map((a) => a.id), contatti,
      });
      setTesto(""); setAllegati([]); setContatti([]);
      onUpdate?.(r.data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Invio non riuscito");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="ticket-messaggi" className="mt-4 pt-4 border-t border-slate-200">
      <button type="button" data-testid="messaggi-toggle" onClick={() => { const next = !open; setOpen(next); if (next && unreadCount > 0) onRead?.(); }}
        className="flex items-center gap-2 text-brand font-bold text-base w-full">
        <MessageCircle className="w-5 h-5" />
        <span>{titleLabel}</span>
        {unreadCount > 0 ? (
          <span data-testid="messaggi-non-letti-badge" className="text-xs font-bold px-2 py-0.5 rounded-full bg-red-600 text-white animate-pulse">{unreadCount} nuovo{unreadCount > 1 ? "i" : ""}</span>
        ) : (messaggi.length > 0 && (
          <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-[#FBEEF3] text-brand border border-[#EBC5D2]">{messaggi.length}</span>
        ))}
        <ChevronDown className={`w-4 h-4 ml-auto transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div className="mt-3 relative" {...dragProps}>
          {isDragOver && (
            <div className="pointer-events-none absolute inset-0 flex items-center justify-center rounded-lg bg-brand/10 z-20 border-2 border-brand border-dashed">
              <div className="flex items-center gap-2 text-brand font-bold text-sm bg-white/95 px-3 py-1.5 rounded-full border-2 border-brand shadow-md">
                <Upload className="w-4 h-4" /> Rilascia per allegare
              </div>
            </div>
          )}
          {messaggi.length > 0 && (
            <div data-testid="messaggi-lista" className="max-h-64 overflow-y-auto space-y-2 mb-3 pr-1">
              {messaggi.map((m) => {
                const isMine = m.autore_ruolo === mine;
                // Iter122s · distinzione tecnico condiviso vs fornitore titolare nella chat
                const isFornMsg = m.autore_ruolo === "Fornitore";
                const isTecnico = isFornMsg && !!m.autore_fornitore_id && tecniciCondivisiIds.includes(m.autore_fornitore_id);
                const isTitolare = isFornMsg && !isTecnico && (!m.autore_fornitore_id || m.autore_fornitore_id === fornitoreTitolareId);
                // Bubble palette: proprio (isMine): brand; altrimenti: base slate,
                // tecnico condiviso in indigo per differenziarlo chiaramente dal fornitore titolare
                let bubbleCls = isMine ? "bg-brand text-white" : "bg-slate-100 text-slate-800";
                if (!isMine && isTecnico) bubbleCls = "bg-indigo-50 text-indigo-900 border-2 border-indigo-300";
                else if (!isMine && isTitolare) bubbleCls = "bg-slate-100 text-slate-800 border border-slate-200";
                return (
                  <div key={m.id} className={`flex ${isMine ? "justify-end" : "justify-start"}`}>
                    <div data-testid={isTecnico ? `messaggio-tecnico-${m.id}` : (isTitolare ? `messaggio-titolare-${m.id}` : undefined)} className={`max-w-[85%] rounded-lg px-3 py-2 text-sm ${bubbleCls}`}>
                      {isFornMsg && (isTecnico || (isTitolare && tecniciCondivisiIds.length > 0)) && (
                        <div className={`inline-flex items-center gap-1 mb-1 text-[10px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded ${isTecnico ? "bg-indigo-600 text-white" : "bg-slate-600 text-white"}`}>
                          {isTecnico ? "Tecnico condiviso" : "Fornitore titolare"}
                        </div>
                      )}
                      {m.testo && <div className="whitespace-pre-wrap break-words">{m.testo}</div>}
                      {(m.allegati || []).length > 0 && (
                        <div className="flex flex-wrap gap-2 mt-2">
                          {m.allegati.map((a) => isImg(a) ? (
                            <button type="button" key={a.id} onClick={() => openSecureFile(`/files/${a.id}`)} className="block w-20 h-20 rounded-lg overflow-hidden border border-black/10">
                              <AuthImage fileId={a.id} className="w-full h-full object-cover" />
                            </button>
                          ) : (
                            <button type="button" key={a.id} onClick={() => openSecureFile(`/files/${a.id}`)}
                              className={`inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-semibold ${isMine ? "bg-white/15 text-white" : "bg-white border border-slate-300 text-brand"}`}>
                              <FileText className="w-4 h-4 shrink-0" /><span className="max-w-[140px] truncate">{a.original_filename}</span><Download className="w-3.5 h-3.5 shrink-0" />
                            </button>
                          ))}
                        </div>
                      )}
                      {(m.contatti || []).length > 0 && (
                        <div className="flex flex-col gap-1 mt-2">
                          {m.contatti.map((c, i) => (
                            <a key={i} href={c.telefono ? `tel:${c.telefono}` : undefined}
                              className={`inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-semibold w-fit ${isMine ? "bg-white/15 text-white" : "bg-white border border-slate-300 text-brand"}`}>
                              <Phone className="w-3.5 h-3.5 shrink-0" /><span>{[c.nome, c.telefono].filter(Boolean).join(" · ")}</span>
                            </a>
                          ))}
                        </div>
                      )}
                      <div className={`text-[11px] mt-1 ${isMine ? "text-white/70" : "text-slate-400"}`}>{m.autore_nome} · {fmt(m.at)}</div>
                    </div>
                  </div>
                );
              })}
              <div ref={endRef} />
            </div>
          )}

          {(allegati.length > 0 || contatti.length > 0) && (
            <div className="flex flex-wrap gap-2 mb-2" data-testid="messaggi-pending">
              {allegati.map((a) => (
                <span key={a.id} className="inline-flex items-center gap-1 pl-2 pr-1 py-1 rounded-full bg-slate-100 border border-slate-300 text-xs text-slate-700">
                  {isImg(a) ? <Camera className="w-3.5 h-3.5" /> : <FileText className="w-3.5 h-3.5" />}
                  <span className="max-w-[120px] truncate">{a.original_filename}</span>
                  <button type="button" onClick={() => setAllegati((p) => p.filter((x) => x.id !== a.id))} className="hover:text-red-600"><X className="w-3.5 h-3.5" /></button>
                </span>
              ))}
              {contatti.map((c, i) => (
                <span key={i} className="inline-flex items-center gap-1 pl-2 pr-1 py-1 rounded-full bg-[#FBEEF3] border border-[#EBC5D2] text-xs text-brand">
                  <Phone className="w-3.5 h-3.5" /><span className="max-w-[140px] truncate">{[c.nome, c.telefono].filter(Boolean).join(" · ")}</span>
                  <button type="button" onClick={() => setContatti((p) => p.filter((_, j) => j !== i))} className="hover:text-red-600"><X className="w-3.5 h-3.5" /></button>
                </span>
              ))}
            </div>
          )}

          {showContatto && (
            <div className="flex flex-wrap items-center gap-2 mb-2 p-2 rounded-lg bg-slate-50 border border-slate-200" data-testid="contatto-form">
              <Input data-testid="contatto-nome" value={cNome} onChange={(e) => setCNome(e.target.value)} placeholder="Nome contatto" className="h-9 flex-1 min-w-[120px]" />
              <Input data-testid="contatto-tel" value={cTel} onChange={(e) => setCTel(e.target.value)} placeholder="Telefono" className="h-9 flex-1 min-w-[120px]" />
              <Button data-testid="contatto-add" size="sm" className="h-9 bg-brand hover:bg-brand-dark" onClick={addContattoManuale}>Aggiungi</Button>
              <Button variant="ghost" size="sm" className="h-9" onClick={() => setShowContatto(false)}><X className="w-4 h-4" /></Button>
            </div>
          )}

          {/* Iter122fl · Dialog Rubrica app (condòmini per chat fornitore, fornitori per chat condòmino) */}
          {rubricaOpen && (
            <div className="fixed inset-0 z-40 bg-black/40 flex items-center justify-center p-3" onClick={() => setRubricaOpen(false)} data-testid="rubrica-overlay">
              <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[80vh] flex flex-col overflow-hidden" onClick={(e) => e.stopPropagation()} data-testid="rubrica-dialog">
                <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200">
                  <div className="font-display font-bold text-lg text-slate-800">Rubrica {rubricaLabel}</div>
                  <button type="button" onClick={() => setRubricaOpen(false)} className="text-slate-400 hover:text-slate-700" data-testid="rubrica-close"><X className="w-5 h-5" /></button>
                </div>
                <div className="px-4 py-3 border-b border-slate-100">
                  <div className="relative">
                    <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                    <Input data-testid="rubrica-search" value={rubricaSearch} onChange={(e) => setRubricaSearch(e.target.value)}
                      placeholder={`Cerca tra i ${rubricaLabel}...`} className="h-10 pl-9" autoFocus />
                  </div>
                </div>
                <div className="flex-1 overflow-y-auto" data-testid="rubrica-list">
                  {rubricaLoading ? (
                    <div className="flex items-center justify-center py-8 text-slate-500"><Loader2 className="w-5 h-5 animate-spin mr-2" /> Caricamento…</div>
                  ) : rubricaError ? (
                    <div className="p-4 text-sm text-red-700 bg-red-50 border-y border-red-200" data-testid="rubrica-error">{rubricaError}</div>
                  ) : rubricaFiltered.length === 0 ? (
                    <div className="p-6 text-center text-sm text-slate-500" data-testid="rubrica-empty">
                      {rubricaSearch ? `Nessun risultato per "${rubricaSearch}".` : `Nessun ${variant === "condomino" ? "fornitore" : "condòmino"} in rubrica.`}
                    </div>
                  ) : (
                    <ul className="divide-y divide-slate-100">
                      {rubricaFiltered.map((c) => (
                        <li key={c.key}>
                          <button type="button" onClick={() => selectFromRubrica(c)} data-testid={`rubrica-item-${c.key}`}
                            className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-[#FBEEF3]/60 transition-colors">
                            <div className="w-9 h-9 rounded-full bg-[#FBEEF3] text-brand flex items-center justify-center shrink-0 font-bold text-sm">
                              {(c.nome || "?").trim().charAt(0).toUpperCase()}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="font-semibold text-slate-800 truncate">{c.nome}</div>
                              <div className="text-xs text-slate-500 truncate flex items-center gap-2 flex-wrap">
                                {c.telefono ? <span className="inline-flex items-center gap-1"><Phone className="w-3 h-3" /> {c.telefono}</span> : <span className="italic text-slate-400">nessun numero</span>}
                                {c.sottotitolo && <span>· {c.sottotitolo}</span>}
                              </div>
                            </div>
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
                <div className="px-4 py-3 border-t border-slate-200 flex justify-between items-center gap-2 flex-wrap bg-slate-50">
                  <div className="text-xs text-slate-500">{rubricaFiltered.length} {rubricaFiltered.length === 1 ? "contatto" : "contatti"}</div>
                  <Button variant="outline" size="sm" onClick={openManuale} data-testid="rubrica-manuale-btn" className="h-9 border-brand/40 text-brand">
                    <UserPlus className="w-4 h-4 mr-1.5" /> Compila manualmente
                  </Button>
                </div>
              </div>
            </div>
          )}

          <div className="flex items-center gap-2">
            {readonly ? (
              <div className="flex items-center gap-2 text-xs text-slate-500 italic px-3 py-2 rounded-lg bg-slate-50 border border-slate-200 w-full" data-testid="messaggi-readonly">
                <MessageCircle className="w-4 h-4" /> Sola lettura — la manutenzione è chiusa. La cronologia dei messaggi resta consultabile.
              </div>
            ) : (
            <>
            <label data-testid="messaggi-file-label" title="Allega foto o file"
              className="inline-flex items-center justify-center h-10 w-10 rounded-lg border-2 border-slate-300 bg-white cursor-pointer hover:border-[#C98AA0] text-slate-600 shrink-0">
              <Paperclip className="w-5 h-5" />
              <input data-testid="messaggi-file-input" type="file" accept="image/*,.pdf,.doc,.docx" multiple className="hidden"
                onChange={(e) => { upload(e.target.files); e.target.value = ""; }} />
            </label>
            <button type="button" data-testid="messaggi-contatto-btn" title={`Aggiungi contatto dalla rubrica ${rubricaLabel}`} onClick={pickContatto}
              className="inline-flex items-center justify-center h-10 w-10 rounded-lg border-2 border-slate-300 bg-white hover:border-[#C98AA0] text-slate-600 shrink-0">
              <UserPlus className="w-5 h-5" />
            </button>
            <Input data-testid="messaggi-input" value={testo} onChange={(e) => setTesto(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); invia(); } }}
              placeholder={`Scrivi a ${other}...`} className="h-10" disabled={busy} />
            <Button data-testid="messaggi-invia" onClick={invia} disabled={busy || uploading || (!testo.trim() && allegati.length === 0 && contatti.length === 0)}
              className="h-10 px-3 bg-brand hover:bg-brand-dark shrink-0">
              <Send className="w-4 h-4" />
            </Button>
            </>
            )}
          </div>
          {uploading && <div className="text-xs text-slate-500 mt-1">Caricamento allegato in corso...</div>}
        </div>
      )}
    </div>
  );
}
